import { useState, useCallback, useMemo } from 'react';
import {
  type ConstitutionType,
  type DiagnosisResult,
  getConstitutionFromScores,
  getElementByConstitution,
} from '@/lib/index';
import { diagnosisQuestions } from '@/data/index';

/**
 * 사상체질 진단 로직 및 상태를 관리하는 커스텀 훅
 */
export const useDiagnosis = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [scores, setScores] = useState<Record<ConstitutionType, number>>({
    '태양인': 0,
    '태음인': 0,
    '소양인': 0,
    '소음인': 0,
  });
  const [isCompleted, setIsCompleted] = useState(false);
  const [result, setResult] = useState<DiagnosisResult | null>(null);

  const totalSteps = diagnosisQuestions.length;
  const currentQuestion = diagnosisQuestions[currentStep];

  /**
   * 진단 진행률 계산 (0-100)
   */
  const progress = useMemo(() => 
    Math.round((currentStep / totalSteps) * 100), 
    [currentStep, totalSteps]
  );

  /**
   * 사용자가 답변을 선택했을 때 점수를 업데이트하고 다음 단계로 이동
   * 마지막 질문일 경우 최종 결과 산출
   */
  const answerQuestion = useCallback((type: ConstitutionType) => {
    setScores((prev) => {
      const updatedScores = {
        ...prev,
        [type]: prev[type] + 1,
      };

      if (currentStep < totalSteps - 1) {
        setCurrentStep((s) => s + 1);
      } else {
        // 최종 체질 진단 결과 생성
        const constitution = getConstitutionFromScores(updatedScores);
        const recommendedProductType = getElementByConstitution(constitution);

        const diagnosisResult: DiagnosisResult = {
          constitution,
          scores: updatedScores,
          recommendedProductType,
          analysisDate: new Date().toISOString(),
        };

        setResult(diagnosisResult);
        setIsCompleted(true);
      }

      return updatedScores;
    });
  }, [currentStep, totalSteps]);

  /**
   * 이전 단계로 이동 (선택 사항)
   * 이전 답변에 대한 점수를 차감하는 로직이 필요할 수 있으나,
   * 정밀도를 위해 일반적으로 뒤로가기 시 점수 초기화 또는 재시작을 권장
   */
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  /**
   * 진단 상태 초기화
   */
  const reset = useCallback(() => {
    setCurrentStep(0);
    setScores({
      '태양인': 0,
      '태음인': 0,
      '소양인': 0,
      '소음인': 0,
    });
    setIsCompleted(false);
    setResult(null);
  }, []);

  /**
   * 섭취 후 피드백 업데이트 (결과 페이지 또는 관리 페이지에서 사용)
   */
  const updateFeedback = useCallback((feedback: NonNullable<DiagnosisResult['userFeedback']>) => {
    if (result) {
      setResult({
        ...result,
        userFeedback: feedback,
      });
    }
  }, [result]);

  return {
    currentStep,
    totalSteps,
    currentQuestion,
    progress,
    isCompleted,
    result,
    scores,
    answerQuestion,
    goToPreviousStep,
    reset,
    updateFeedback,
  };
};
